import { Fragment, ReactNode, useMemo } from "react";
import { styled } from "@pigment-css/react";
import { CalendarPlus, CompleteIcon, SquarePlus, ThumbsDown } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { BottomSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";

const InnerCard = styled("div")(({ theme }) => ({
  background: theme.fieldSalesTwo.colors.surface.glass,
  borderRadius: theme.fieldSalesTwo.radius[38],
  padding: `${theme.fieldSalesTwo.spacing[32]} ${theme.fieldSalesTwo.spacing[20]}`,
  marginInline: theme.fieldSalesTwo.spacing[12],
  display: "flex",
  flexDirection: "column",
}));

const ItemButton = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingBlock: theme.fieldSalesTwo.spacing[8],
  paddingInline: theme.fieldSalesTwo.spacing[12],
  marginInline: `-${theme.fieldSalesTwo.spacing[12]}`,
  background: "transparent",
  border: "none",
  borderRadius: theme.fieldSalesTwo.radius[16],
  textAlign: "left",
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  color: theme.fieldSalesTwo.colors.neutral[900],
  transition: "background-color 120ms ease",
  "&:hover:not(:disabled)": {
    backgroundColor: theme.fieldSalesTwo.colors.neutral[200],
  },
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: `-${theme.strokeWidth.regular}`,
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
}));

const Divider = styled("span")(({ theme }) => ({
  height: theme.strokeWidth.thin,
  marginBlock: theme.fieldSalesTwo.spacing[8],
  background: theme.fieldSalesTwo.colors.neutral[200],
}));

const IconTile = styled("span")(({ theme }) => ({
  flexShrink: 0,
  width: "56px",
  height: "52px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.fieldSalesTwo.radius[16],
  background: "rgba(19, 19, 19, 0.04)",
  color: theme.fieldSalesTwo.colors.neutral[900],
  "& svg": {
    width: theme.fieldSalesTwo.spacing[20],
    height: theme.fieldSalesTwo.spacing[20],
  },
}));

const TextStack = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[2],
  minWidth: 0,
  flex: 1,
  paddingTop: theme.fieldSalesTwo.spacing[2],
}));

const ItemTitle = styled("span")(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const ItemDescription = styled("span")(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[168],
  color: theme.fieldSalesTwo.colors.neutral[800],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

interface ActionDef {
  key: "planMeeting" | "createTodo" | "markAsDone" | "dismiss";
  icon: ReactNode;
  disabled?: boolean;
  onSelect?: () => void;
}

interface CustomerActionsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onMarkAsDone?: () => void;
  onDismiss?: () => void;
  onPlanMeeting?: () => void;
  onCreateTodo?: () => void;
}

export function CustomerActionsSheet({
  isOpen,
  onOpenChange,
  onMarkAsDone,
  onDismiss,
  onPlanMeeting,
  onCreateTodo,
}: CustomerActionsSheetProps) {
  const t = useTranslations("fieldSalesTwo.customerActions");

  const actions = useMemo<ActionDef[]>(
    () => [
      { key: "planMeeting", icon: <CalendarPlus />, onSelect: onPlanMeeting },
      { key: "createTodo", icon: <SquarePlus />, onSelect: onCreateTodo },
      { key: "markAsDone", icon: <CompleteIcon />, onSelect: onMarkAsDone },
      { key: "dismiss", icon: <ThumbsDown />, onSelect: onDismiss },
    ],
    [onMarkAsDone, onDismiss, onPlanMeeting, onCreateTodo],
  );

  const handleSelect = (action: ActionDef) => {
    if (action.disabled) return;
    action.onSelect?.();
    onOpenChange(false);
  };

  return (
    <BottomSheet isOpen={isOpen} onOpenChange={onOpenChange} title={t("title")} closeAriaLabel={t("closeAriaLabel")}>
      <InnerCard>
        {actions.map((action, index) => (
          <Fragment key={action.key}>
            <ItemButton type="button" disabled={action.disabled} onClick={() => handleSelect(action)}>
              <IconTile>{action.icon}</IconTile>
              <TextStack>
                <ItemTitle>{t(`${action.key}.title`)}</ItemTitle>
                <ItemDescription>{t(`${action.key}.description`)}</ItemDescription>
              </TextStack>
            </ItemButton>
            {index < actions.length - 1 && <Divider aria-hidden="true" />}
          </Fragment>
        ))}
      </InnerCard>
    </BottomSheet>
  );
}
